/**
 * NotFound — 404 / offline fallback page.
 *
 * Simplified per user direction: no decorative numeral, no small eyebrow —
 * the routing message IS the headline. Full-bleed step-and-repeat wordmark
 * pattern behind a centered card.
 *
 * Props:
 *   variant = "not-found" (default) | "offline"
 *     Only changes the headline + body copy so this single component can
 *     back both states (catch-all `*` + any future service-worker offline
 *     fallback).
 *
 *   `useNoIndex` is applied so search engines never index a 404 URL.
 */

import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, RefreshCw } from "lucide-react";
import { useNoIndex } from "../lib/useNoIndex";

const COPY = {
  "not-found": {
    numeral: "404",
    title: "Lost on Campus.",
    body:  "The page you were looking for doesn't exist. Or maybe it graduated. Either way, let's get you back to the main quad.",
    primary: { to: "/", label: "Take me home" },
  },
  offline: {
    numeral: "Offline",
    title: "Signal Dropped.",
    body:  "We can't reach the server right now. Check your connection, then try again. Nothing's lost.",
    primary: { to: "/", label: "Try again" },
  },
};

export default function NotFound({ variant = "not-found" }) {
  useNoIndex();
  const c = COPY[variant] || COPY["not-found"];
  const isOffline = variant === "offline";

  return (
    <main
      data-testid={`page-${variant}`}
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-yellow px-6 py-20"
      style={{
        backgroundImage: 'url("/step-and-repeat.webp")',
        backgroundSize: "560px auto",
        backgroundRepeat: "repeat",
      }}
    >
      {/* Slight yellow wash so the card contrast holds regardless of where
          the pattern tile lands under it. */}
      <span aria-hidden className="pointer-events-none absolute inset-0 bg-yellow/25" />

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-2xl"
      >
        <div
          className="relative rounded-2xl border border-ink/10 bg-sand p-10 text-center shadow-[0_20px_60px_-20px_rgba(26,23,20,0.35)] lg:p-14"
          data-testid={`${variant}-card`}
        >
          <h1
            data-testid={`${variant}-title`}
            className="font-serif leading-[0.98] tracking-tight text-ink"
          >
            <span
              data-testid={`${variant}-numeral`}
              className="block text-[3.5rem] lg:text-[5.5rem]"
            >
              {c.numeral}
            </span>
            <span className="mt-3 block text-3xl lg:text-[3.5rem]">
              {c.title}
            </span>
          </h1>
          <p
            data-testid={`${variant}-body`}
            className="mx-auto mt-6 max-w-lg text-base leading-relaxed text-ink/70"
          >
            {c.body}
          </p>

          <div className="mt-10 flex items-center justify-center">
            {isOffline ? (
              <button
                type="button"
                onClick={() => window.location.reload()}
                data-testid="retry-button"
                className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-base font-medium text-sand transition hover:bg-ink/90"
              >
                <RefreshCw className="h-4 w-4" strokeWidth={1.75} />
                {c.primary.label}
              </button>
            ) : (
              <Link
                to={c.primary.to}
                data-testid="home-button"
                className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-base font-medium text-sand transition hover:bg-ink/90"
              >
                {c.primary.label}
                <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
              </Link>
            )}
          </div>

          {/* Small palette-oval accent — matches the DoodleDivider vocabulary. */}
          <div aria-hidden className="mt-10 flex items-center justify-center gap-2">
            <span className="h-1.5 w-4 rounded-full bg-tangerine" />
            <span className="h-1.5 w-4 rounded-full bg-cobalt" />
            <span className="h-1.5 w-4 rounded-full bg-grass" />
            <span className="h-1.5 w-4 rounded-full bg-blossom" />
            <span className="h-1.5 w-4 rounded-full bg-yellow ring-1 ring-inset ring-ink/20" />
          </div>
        </div>
      </motion.div>
    </main>
  );
}
